import { useEffect, useRef, useState } from 'react'

import type { Trip } from '../../api/types'
import { useNetzbild } from '../../hooks/useNetzbild'
import { ausschnittAus, grundStil } from '../../lib/ausschnitt'

/**
 * Der Hintergrund einer Reise -- Farbe und Bild, hinter dem Inhalt.
 *
 * **Die eine Stelle** (1.58.0). Die Rechnung stand eingebacken in
 * `ThreadPage.tsx`: die Probe mit einem `Image`, der Deckgrad, der gewählte
 * Ausschnitt, die gemessene Fläche und die beiden `div`s. Mit der
 * Playlist-Seite (#102) hätte sie ein zweites Mal dagestanden -- und die
 * Frage ist nach `CLAUDE.md` nicht „wo noch?", sondern „warum steht es
 * zweimal da?". Also steht es einmal hier, und beide Seiten hängen daran.
 *
 * **Er misst sich selbst.** `.chatgrund` liegt auf `inset: 0` im Rahmen
 * darüber, seine eigene Größe *ist* also die Fläche; die Seite muss dafür
 * nichts halten und nichts hereinreichen. Vorher trug `ThreadPage` einen
 * `ref` und einen Zustand allein für diese Zahl.
 *
 * **Ein Hintergrund ist ein `<div>` und hat kein `onError`.** Das Bild wird
 * deshalb mit einem `Image` daneben geprüft, damit ein fehlgeschlagener
 * Ladeversuch bekannt ist und die Adresse einen Zähler bekommt, sobald das
 * Netz zurück ist (`useNetzbild`, 1.50.0).
 *
 * **Und diese vier Hooks haben schon einmal die Anwendung weiß gemacht.** Sie
 * standen in `ThreadPage` hinter den Ausstiegen: der erste Durchlauf rief
 * alle, der zweite nahm nach einer gescheiterten Abfrage den Ausstieg davor,
 * React zählte weniger Hooks als beim Mal davor und warf (#300, „rendered
 * fewer hooks than expected"). Aus dem Betrieb: „kurze Anzeige: wird geladen,
 * dann nur ein weißer Bildschirm". Behoben war es damals, indem sie nach oben
 * wanderten -- hier können sie gar nicht mehr hinter einen Ausstieg geraten,
 * denn die Seite ruft eine Komponente und keine Hooks. Wer den Hintergrund
 * wieder in eine Seite einbaut, holt sich die Regel zurück: **Hooks stehen
 * vor jedem `return`.**
 */
export function Chathintergrund({ grund }: { grund: Trip['chat_bg'] | undefined }) {
  /*
   * **Der Kasten als Zustand und nicht als `useRef`**, und der Grund ist ein
   * roter Lauf: dieses Bauteil kehrt früh mit `null` zurück, solange die
   * Reise noch lädt. Ein Effekt mit leerer Abhängigkeitsliste sah damit beim
   * ersten Durchlauf nichts und lief danach nie wieder -- die Fläche blieb
   * 0 × 0, und `grundStil` gab `cover` statt der gerechneten Größe.
   * `ausschnitt.mjs` hat es gemessen („NaN × NaN Bild in 390 × 724 Fläche").
   * Ein Rückruf am `ref` zeichnet neu, sobald der Kasten wirklich dasteht.
   */
  const [kasten, setKasten] = useState<HTMLDivElement | null>(null)
  const [flaeche, setFlaeche] = useState({ breite: 0, hoehe: 0 })

  const probe = useRef<HTMLImageElement | null>(null)
  const bild = useNetzbild(
    grund?.url ?? null,
    // Die Probe ist der Zeuge des Bildes: `complete` ohne Breite heißt, dass
    // es nie kam (1.50.1) -- ob nun ein `error` gefeuert hat oder nicht.
    () => probe.current !== null && probe.current.complete && probe.current.naturalWidth === 0,
  )

  useEffect(() => {
    if (kasten === null) return
    const messe = () => setFlaeche({ breite: kasten.clientWidth, hoehe: kasten.clientHeight })
    messe()
    const wache = new ResizeObserver(messe)
    wache.observe(kasten)
    return () => wache.disconnect()
  }, [kasten])

  useEffect(() => {
    if (bild.adresse === null) return
    const wer = new Image()
    wer.onerror = bild.fehlgeschlagen
    wer.src = bild.adresse
    probe.current = wer
    return () => {
      wer.onerror = null
    }
    // Nur die Adresse zählt; der Rückruf ist für eine Adresse derselbe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bild.adresse])

  if (grund === undefined || grund === null) return null
  if (grund.color === null && grund.url === null) return null

  /*
   * Der Deckgrad liegt am Bild und nicht an der Farbe: die Farbe ist das,
   * worauf das Bild liegt, und soll deckend bleiben.
   */
  const aussen = grund.color !== null ? { background: grund.color } : {}
  const aufBild =
    grund.url === null
      ? null
      : {
          backgroundImage: `url(${JSON.stringify(bild.adresse ?? grund.url)})`,
          opacity: Math.max(0, Math.min(100, grund.opacity)) / 100,
          // **Der gewählte Ausschnitt** (#51) -- gerechnet, nicht als
          // `cover`: nur so kann das Bild auch kleiner sein als die Fläche
          // und das ganze Motiv auf der Farbe stehen.
          ...grundStil(ausschnittAus(grund), flaeche, { breite: grund.width, hoehe: grund.height }),
        }

  return (
    <div ref={setKasten} className="chatgrund" aria-hidden="true" style={aussen}>
      {aufBild !== null && (
        <div className="chatgrund__bild" style={aufBild} data-netzbild-versuch={bild.versuch} />
      )}
    </div>
  )
}
